import { Link, useParams } from "react-router-dom";
import { DAYS } from "@/data/itinerary";
import WeatherWidget from "@/components/WeatherWidget";
import Gallery from "@/components/Gallery";
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  Info,
  Lightbulb,
  MapPin,
  Utensils,
  Waves,
  Wind,
} from "lucide-react";
import { useEffect, useState } from "react";
import { STORAGE_KEYS, storage } from "@/lib/storage";
import { toast } from "sonner";
import { useLiveWind } from "@/hooks/useLiveWind";

function formatDate(iso) {
  const d = new Date(iso);
  return d
    .toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })
    .replace(/^./, (c) => c.toUpperCase());
}

export default function DayDetail() {
  const { id } = useParams();
  const day = DAYS.find((d) => String(d.id) === String(id));
  const prev = day ? DAYS.find((d) => d.id === day.id - 1) : null;
  const next = day ? DAYS.find((d) => d.id === day.id + 1) : null;

  const [note, setNote] = useState("");
  const { wind } = useLiveWind();

  useEffect(() => {
    if (!day) return;
    const all = storage.get(STORAGE_KEYS.diary, {});
    setNote(all[day.id] || "");
  }, [day]);

  if (!day) {
    return (
      <div className="pt-32 pb-24 max-w-3xl mx-auto px-6 text-center">
        <div className="font-display text-4xl text-[#1D3A4C]">
          Giornata non trovata
        </div>
        <Link
          to="/itinerario"
          className="mt-6 inline-block text-[#D96C4A] underline"
        >
          Torna all'itinerario
        </Link>
      </div>
    );
  }

  const saveNote = () => {
    const all = storage.get(STORAGE_KEYS.diary, {});
    all[day.id] = note;
    storage.set(STORAGE_KEYS.diary, all);
    toast.success("Appunto salvato nel diario");
  };

  return (
    <div data-testid={`day-detail-${day.id}`} className="pb-24">
      {/* Cover */}
      <section className="relative h-[70vh] min-h-[520px] w-full overflow-hidden">
        <img
          src={day.cover}
          alt={day.title}
          className="absolute inset-0 w-full h-full object-cover animate-kb"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/70" />
        <div className="grain absolute inset-0" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-16">
          <div className="font-accent text-xs text-white/80">
            Giorno {day.id} · {day.zone}
          </div>
          <h1 className="mt-3 font-display text-5xl md:text-7xl text-white leading-[0.95] max-w-4xl">
            {day.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-5 text-white/85">
            <div className="inline-flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4" />
              {formatDate(day.date)}
            </div>
            <div className="inline-flex items-center gap-2 text-sm">
              <MapPin className="w-4 h-4" />
              {day.coords.label}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-14 grid lg:grid-cols-3 gap-14">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-14">
          <p className="text-lg text-[#1D3A4C] leading-relaxed font-light">
            {day.story}
          </p>

          <div className="p-6 rounded-2xl bg-[#F5EFE6] border border-[#1D3A4C]/10">
            <div className="font-accent text-xs text-[#D96C4A] mb-2">
              Meteo previsto
            </div>
            <WeatherWidget
              lat={day.coords.lat}
              lng={day.coords.lng}
              date={day.date}
            />
          </div>

          {/* Timeline */}
          <div>
            <div className="font-accent text-xs text-[#D96C4A] mb-4">
              La giornata
            </div>
            <h2 className="font-display text-3xl text-[#1D3A4C] mb-8">
              Ora per ora
            </h2>
            <ol className="relative border-l border-[#1D3A4C]/15 pl-6 space-y-6">
              {day.timeline.map((t, i) => (
                <li
                  key={i}
                  data-testid={`day-${day.id}-timeline-${i}`}
                  className="relative"
                >
                  <span className="absolute -left-[31px] top-1.5 w-3 h-3 rounded-full bg-[#D96C4A] ring-4 ring-[#D96C4A]/15" />
                  <div className="font-accent text-xs text-[#D96C4A]">
                    {t.time}
                  </div>
                  <div className="mt-1 text-[#1D3A4C]">{t.text}</div>
                </li>
              ))}
            </ol>
          </div>

          {/* Beaches */}
          {day.beaches.length > 0 && (
            <div>
              <div className="font-accent text-xs text-[#D96C4A] mb-4">
                Spiagge & calette
              </div>
              <h2 className="font-display text-3xl text-[#1D3A4C] mb-8">
                Dove tuffarsi
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {day.beaches.map((b, i) => {
                  const favored = wind && b.wind?.includes(wind.name);
                  return (
                  <div
                    key={i}
                    data-testid={`day-${day.id}-beach-${i}`}
                    className={`p-6 rounded-2xl border bg-white hover:shadow-md transition-shadow ${
                      favored
                        ? "border-[#D96C4A]/40 ring-1 ring-[#D96C4A]/20"
                        : "border-[#1D3A4C]/10"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Waves
                        className="w-5 h-5 text-[#D96C4A] mt-1 shrink-0"
                        strokeWidth={1.5}
                      />
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-display text-xl text-[#1D3A4C]">
                            {b.name}
                          </div>
                          {favored && (
                            <span
                              data-testid={`day-${day.id}-beach-${i}-favored`}
                              className="px-2 py-0.5 rounded-full bg-[#D96C4A] text-white text-[10px] font-accent tracking-wide uppercase"
                            >
                              Consigliata oggi
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#5C7180] mt-2 leading-relaxed">
                          {b.note}
                        </p>
                        {b.wind?.length > 0 && (
                          <div className="mt-3 flex flex-wrap items-center gap-1.5">
                            <Wind className="w-3.5 h-3.5 text-[#8A9BA8]" />
                            {b.wind.map((w) => (
                              <span
                                key={w}
                                className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                                  wind?.name === w
                                    ? "bg-[#D96C4A] text-white"
                                    : "bg-[#1D3A4C]/5 text-[#1D3A4C]"
                                }`}
                              >
                                {w}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Gallery */}
          <Gallery dayId={day.id} photos={day.gallery} />

          {/* Restaurants */}
          {day.restaurants.length > 0 && (
            <div>
              <div className="font-accent text-xs text-[#D96C4A] mb-4">
                A tavola
              </div>
              <h2 className="font-display text-3xl text-[#1D3A4C] mb-8">
                Dove mangiare
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {day.restaurants.map((r, i) => (
                  <div
                    key={i}
                    data-testid={`day-${day.id}-restaurant-${i}`}
                    className="p-6 rounded-2xl border border-[#1D3A4C]/10 bg-white"
                  >
                    <div className="flex items-start gap-3">
                      <Utensils
                        className="w-5 h-5 text-[#1D3A4C] mt-1 shrink-0"
                        strokeWidth={1.5}
                      />
                      <div>
                        <div className="font-display text-lg text-[#1D3A4C]">
                          {r.name}
                        </div>
                        <div className="text-xs text-[#8A9BA8] mt-1">
                          {r.where}
                        </div>
                        <p className="text-sm text-[#5C7180] mt-2">{r.tip}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-1 space-y-8 lg:sticky lg:top-24 lg:self-start">
          {/* POI */}
          {day.poi.length > 0 && (
            <div className="p-6 rounded-2xl bg-white border border-[#1D3A4C]/10">
              <div className="flex items-center gap-2 mb-4">
                <Info className="w-4 h-4 text-[#D96C4A]" />
                <div className="font-accent text-xs text-[#1D3A4C]">
                  Da non perdere
                </div>
              </div>
              <ul className="space-y-2">
                {day.poi.map((p, i) => (
                  <li
                    key={i}
                    className="text-sm text-[#1D3A4C] leading-relaxed"
                  >
                    ✦ {p}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tips */}
          {day.tips.length > 0 && (
            <div className="p-6 rounded-2xl bg-[#1D3A4C] text-white">
              <div className="flex items-center gap-2 mb-4">
                <Lightbulb className="w-4 h-4 text-[#F4C89F]" />
                <div className="font-accent text-xs text-white/70">
                  Consigli d'oro
                </div>
              </div>
              <ul className="space-y-3">
                {day.tips.map((t, i) => (
                  <li key={i} className="text-sm leading-relaxed">
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Diary note */}
          <div className="p-6 rounded-2xl bg-[#F5EFE6] border border-[#1D3A4C]/10">
            <div className="font-accent text-xs text-[#D96C4A] mb-3">
              Appunti della giornata
            </div>
            <textarea
              data-testid={`day-${day.id}-note-input`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Cos'è successo di bello oggi?"
              rows={6}
              className="w-full bg-transparent border-b border-[#1D3A4C]/20 focus:border-[#D96C4A] outline-none py-2 text-[#1D3A4C] placeholder-[#8A9BA8] resize-none transition-colors"
            />
            <button
              data-testid={`day-${day.id}-note-save`}
              onClick={saveNote}
              className="mt-4 w-full px-4 py-2.5 rounded-full bg-[#D96C4A] hover:bg-[#b8542f] text-white text-sm font-medium transition-colors"
            >
              Salva nel diario
            </button>
          </div>
        </aside>
      </div>

      {/* Prev / Next */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-20 flex items-center justify-between gap-6 flex-wrap">
        {prev ? (
          <Link
            to={`/giorno/${prev.id}`}
            data-testid="day-nav-prev"
            className="group inline-flex items-center gap-3 text-[#1D3A4C] hover:text-[#D96C4A] transition-colors"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            <div>
              <div className="font-accent text-[10px] text-[#8A9BA8]">
                Precedente
              </div>
              <div className="font-display">{prev.title}</div>
            </div>
          </Link>
        ) : (
          <span />
        )}
        {next && (
          <Link
            to={`/giorno/${next.id}`}
            data-testid="day-nav-next"
            className="group inline-flex items-center gap-3 text-right text-[#1D3A4C] hover:text-[#D96C4A] transition-colors"
          >
            <div>
              <div className="font-accent text-[10px] text-[#8A9BA8]">
                Successivo
              </div>
              <div className="font-display">{next.title}</div>
            </div>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        )}
      </div>
    </div>
  );
}
